/* eslint-disable no-unused-vars */
/* global Bugsnag */

const URLS = {
  api: typeof API_URL === 'string' ? API_URL : 'https://api.americandiscountvacations.com/',
  base: typeof BASE_URL === 'string' ? BASE_URL : 'https://www.americandiscountvacations.com/',
  images: typeof IMAGES_URL === 'string' ? IMAGES_URL : 'https://img.americandiscountvacations.com/'
};

// URLs sent from the server are generated to end with a slash, therefore
// any paths appended to the url should not have a slash in the beginning.
// This makes sure to remove that slash

window.loadUrl = (type, path) => {
  let url = URLS[type] || '';
  if (path) {
    // Remove first slash if exists
    url += path.replace(/^\//g, '');
  }
  return url;
};

// The image an admin set, or the "Image coming soon" placeholder when there is none (#3225).
// Keep the path in step with PLACEHOLDER_IMAGE in application/config/constants.php.
window.imageOrPlaceholderUrl = (path) => window.loadUrl('images', path || 'cruiselines/ship-image-coming-soon.png');

window.createCookie = (name, cookieString, minutes, customExpiration) => {
  let date, expires;
  if (minutes) {
    date = new Date();
    date.setTime(date.getTime() + minutes * 60 * 1000); // minutes, seconds, milliseconds
    expires = '; expires=' + date.toGMTString();
  } else if (customExpiration) {
    expires = '; expires=' + customExpiration;
  } else {
    expires = '';
  }
  const host = window.location.host;
  const hostParts = host.split('.').reverse();
  const domain = hostParts[1] + '.' + hostParts[0];
  const cookie = name + '=' + cookieString + expires + '; domain=.' + domain + '; path=/';
  document.cookie = cookie;
};

window.adcReadCookie = (name) => {
  const nameEQ = name + '=';
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

/**
 * Reduce a cookie-sourced phone number to phone-safe characters, falling back when what
 * survives is not a number.
 *
 * The character whitelist alone is not enough. geoPhoneOpts is client-writable, and a
 * tampered value such as '<img src=x onerror="...">' reduces to the fragment '.1', which
 * is non-empty and so satisfied the old "|| fallback" check, and was then rendered to the
 * visitor as their phone number. Every number this site serves carries 11 digits, so
 * requiring 7 accepts all of them and rejects the fragments.
 *
 * Lives here rather than in each caller because geolocation.js and adc-qtip.js both need
 * it and both already depend on this file for adcReadCookie/createCookie.
 */
window.adcSanitizePhone = (value) => {
  // Must already be a string. String() would coerce a JSON number or array from the
  // client-writable cookie into something that passes, while callers using this as a
  // predicate keep the original record. Nothing in this repo calls a string method on
  // that value today, so it would surface as a wrong number rather than a throw, but
  // adv-frontend showed the failure mode: three components there call phoneNum.replace()
  // and a numeric value threw during render.
  if (typeof value !== 'string') return '';
  const raw = value.trim();
  const cleaned = raw.replace(/[^\d\s+().-]/g, '').trim();
  // Reject rather than clean. Returning the residue let a value the whitelist had to strip,
  // such as '<script>1234567</script>', pass as '1234567', and callers that use this as a
  // predicate keep the original record, so the raw string is what gets painted. A real
  // number contains only whitelisted characters already, so requiring the value to survive
  // untouched rejects nothing legitimate.
  if (cleaned !== raw) return '';
  // A plus belongs only at the start, as in +1-732-970-9142: a tampered '++1-...' would
  // otherwise reach the tel: links as tel:++1..., which does not dial.
  if (cleaned.indexOf('+', 1) !== -1) return '';
  return (cleaned.match(/\d/g) || []).length >= 7 ? cleaned : '';
};

/** The sanitized number, or the given fallback when it is not phone-like. */
window.adcPhoneOrFallback = (value, fallback) => window.adcSanitizePhone(value) || fallback;

/* Track page teardown so a request cancelled by the visitor leaving can be told apart
 * from one that genuinely failed while the page was still up. pagehide is used rather
 * than beforeunload because a beforeunload listener disqualifies the page from the
 * back/forward cache in several browsers. */
let pageIsUnloading = false;
// Status 0 reports for this page load, keyed by host. See reportNoResponse below.
let noResponseReports = {};
window.addEventListener('pagehide', () => {
  pageIsUnloading = true;
  flushNoResponseReports();
});

/* On mobile, hidden is often the last event a page reliably gets before it is discarded,
 * so a status 0 report still waiting on its probe is sent then too. */
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') {
    flushNoResponseReports();
  }
});

/* A page put into the back/forward cache is restored without reloading, so the flag set
 * on the way out has to be cleared on the way back in. Without this, every later failure
 * on a restored page would be read as the visitor leaving and go unreported. pageshow
 * fires both on a bfcache restore and on an ordinary load, so this covers both.
 *
 * A restore is also a new page load as far as the status 0 report is concerned. Only a
 * restore clears it: an ordinary load's pageshow can fire after the first failures have
 * already been collected. */
window.addEventListener('pageshow', (event) => {
  pageIsUnloading = false;
  if (event && event.persisted) {
    noResponseReports = {};
  }
});

/* jQuery is loaded as a blocking script in the page head, ahead of this file, so if it
 * is missing by the time we run then its request failed outright rather than merely
 * being slow. Every jQuery-dependent script on the page is about to throw its own
 * ReferenceError, which is how this reaches Bugsnag today: several hundred scattered
 * "$ is not defined" reports with nothing pointing at the underlying cause. Report the
 * cause once instead, with the timing entry for the jQuery request so a dropped
 * transfer can be told apart from a 404 or a client-side blocker. */
const reportMissingJQuery = () => {
  if (typeof Bugsnag === 'undefined' || !Bugsnag.notify) {
    return;
  }

  let timing = null;
  if (window.performance && typeof window.performance.getEntriesByType === 'function') {
    // Matches jquery.min.js without also matching jquery-ui.min.js or jquery.qtip.min.js.
    const entry = window.performance
      .getEntriesByType('resource')
      .find((resource) => resource.name.indexOf('jquery.min.js') !== -1);

    if (entry) {
      timing = {
        duration: Math.round(entry.duration),
        transferSize: entry.transferSize,
        encodedBodySize: entry.encodedBodySize,
        responseEnd: Math.round(entry.responseEnd)
      };
    }
  }

  Bugsnag.notify(new Error('jQuery failed to load'), function (event) {
    event.addMetadata('jquery', {
      // No entry at all means the request was never made or was discarded before it
      // could be timed; an entry with transferSize 0 means it was attempted and failed.
      requestTimed: timing !== null,
      timing,
      connection: (navigator.connection || {}).effectiveType || 'unknown',
      // True when the browser declares itself automated (headless Chrome, WebDriver), to
      // tell crawlers apart from visitors. A bot can hide it, so false proves nothing.
      webdriver: typeof navigator.webdriver === 'boolean' ? navigator.webdriver : 'unknown',
      readyState: document.readyState
    });
  });

  // Read by bugsnag-config.js, which drops the "$ is not defined" reports that follow:
  // they repeat this cause once per script on the page and say nothing it does not.
  window.adcMissingJQueryReported = true;
};

const RESPONSE_TEXT_LIMIT = 200;
// PHP's error output, plain ("Warning: ...") or with html_errors on ("<br />\n<b>Warning</b>: ...").
const PHP_ERROR_PATTERN = /^\s*(?:<br\s*\/?>\s*)?(?:<b>)?\s*(?:PHP )?(?:Fatal error|Parse error|Warning|Notice|Deprecated)(?:<\/b>)?:/i;
const HOST_PROBE_TIMEOUT_MS = 3000;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/* The request URL resolved against the page, or null when it cannot be parsed. */
const parseAjaxUrl = (url) => {
  try {
    return new URL(url, window.location.href);
  } catch (e) {
    return null;
  }
};

/* The request's host and path, with the query string dropped and numeric or UUID segments
 * collapsed to :id, so /ships/123 and /ships/456 count as one endpoint. Every variable
 * segment the browser sends to the API today is a numeric ID; the slug routes such as
 * get-by-url/{urlName} are called server-side from Api.php. Collapsing only ID-shaped
 * segments leaves segments like v2 or oauth2 alone. */
const normalizeAjaxEndpoint = (parsed) => parsed.host + parsed.pathname
  .split('/')
  .map((segment) => (/^\d+$/.test(segment) || UUID_PATTERN.test(segment) ? ':id' : segment))
  .join('/');

/* A message or snippet from a response, capped. A database error is cut to its SQLSTATE
 * code: the API returns Laravel's QueryException and PDOException messages verbatim, and
 * a QueryException message ends with the failing SQL with the visitor's values bound in,
 * plus connection details. The driver message before it can quote a value too, such as
 * the duplicate entry in an integrity error, so none of it is kept. */
const safeResponseText = (text) => {
  if (typeof text !== 'string') {
    return undefined;
  }
  const sqlState = text.match(/SQLSTATE\[[0-9A-Za-z]+\]/);
  if (sqlState) {
    return sqlState[0];
  }
  return text.length > RESPONSE_TEXT_LIMIT ? text.slice(0, RESPONSE_TEXT_LIMIT) + '...' : text;
};

/* The X-Request-Id the API sends with each response (adc-api#2837), or null.
 *
 * Read from getAllResponseHeaders(), which lists only the headers the browser may expose
 * to this page. getResponseHeader() on a cross-origin header the API has not exposed
 * returns null too, but Chrome also logs "Refused to get unsafe header" for it, which
 * would land in every visitor's console and in Bugsnag's breadcrumbs. */
const readRequestId = (jqXHR) => {
  const headers = typeof jqXHR.getAllResponseHeaders === 'function' ? jqXHR.getAllResponseHeaders() : null;
  const match = typeof headers === 'string' ? headers.match(/^x-request-id:[ \t]*(.+?)[ \t]*$/im) : null;
  return match ? match[1].slice(0, 100) : null;
};

/* Where a PHP error happened, as "Warning in /path/file.php on line 12", or undefined when
 * the output is not in that standard form. The message itself is left out: it can quote a
 * value, such as the key in `Undefined array key "..."`, and the kind, file and line are
 * what finding the fault needs. What follows the error is our own output and is dropped. */
const phpErrorLine = (text) => {
  const line = text.replace(/^\s*(?:<br\s*\/?>\s*)+/i, '').split(/<br\s*\/?>|\r?\n/i)[0];
  const plain = line.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
  const where = plain.match(/^(?:PHP )?(Fatal error|Parse error|Warning|Notice|Deprecated):.*? in (\S+) on line (\d+)/i);
  return where ? `${where[1]} in ${where[2]} on line ${where[3]}` : undefined;
};

/* What Bugsnag keeps of a failed response: an allowlisted summary, never the body.
 *
 * A body is a copy of whatever the server sent back, which for a form submission can
 * describe what the visitor entered, and for a database error can contain it outright.
 * What diagnosing a failure needs is smaller:
 *
 * - JSON: the message, and the names of the fields in `errors` but not their messages,
 *   which can echo the submitted value. Everything else, such as `data`, is dropped.
 * - PHP error output ahead of our own response ("Warning: ... on line 12"), plain or in
 *   PHP's HTML form: its kind, file and line, not its message.
 * - HTML, typically a WAF or gateway page answering before the app: the title, which
 *   says which layer answered ("406 Not Acceptable", "502 Bad Gateway"). From the API
 *   host such a page usually lacks CORS headers and arrives as status 0 with no body, so
 *   this mostly serves same-origin requests.
 * - Any other text: nothing beyond its kind. It could be anything, including a JSON value
 *   sent without a JSON content type.
 *
 * The content type and length are always kept, along with the response's request ID.
 * The ID is what leads from this report to the API's own log entry and Bugsnag report,
 * which hold the full exception, so nothing more of the body is needed here. */
const summarizeResponse = (jqXHR) => {
  const text = typeof jqXHR.responseText === 'string' ? jqXHR.responseText : '';
  const contentType = typeof jqXHR.getResponseHeader === 'function' ? jqXHR.getResponseHeader('Content-Type') : null;
  const summary = {
    kind: 'empty',
    contentType: contentType || null,
    length: text.length,
    requestId: readRequestId(jqXHR)
  };
  if (!text) {
    return summary;
  }

  // Parsed whatever the content type says, so a JSON value sent as text/plain or with no
  // content type is still held to the JSON allowlist rather than read as text.
  let json = jqXHR.responseJSON;
  if (json === undefined) {
    try {
      json = JSON.parse(text);
    } catch (e) {
      json = undefined;
    }
  }

  // Any parsed JSON counts, not just an object. A top-level string, number or array is
  // still JSON and can still carry what the visitor submitted, so only an object's
  // allowlisted fields are ever read.
  if (json !== undefined) {
    summary.kind = 'json';
    if (!json || typeof json !== 'object' || Array.isArray(json)) {
      return summary;
    }
    const message = safeResponseText(json.message);
    if (message !== undefined) {
      summary.message = message;
    }
    if (json.errors && typeof json.errors === 'object' && !Array.isArray(json.errors)) {
      summary.errorFields = Object.keys(json.errors).slice(0, 20);
    }
    return summary;
  }

  if (PHP_ERROR_PATTERN.test(text)) {
    summary.kind = 'text';
    summary.snippet = safeResponseText(phpErrorLine(text));
    return summary;
  }

  if (/^\s*</.test(text)) {
    summary.kind = 'html';
    const title = text.match(/<title[^>]*>([^<]*)<\/title>/i);
    if (title) {
      summary.title = safeResponseText(title[1].replace(/\s+/g, ' ').trim());
    }
    return summary;
  }

  summary.kind = 'text';
  return summary;
};

/* Decide how a failed request is filed in Bugsnag.
 *
 * Left to itself, Bugsnag groups by error class and stack location, which scattered a
 * single underlying condition across hundreds of groups: a genuine API outage would show
 * up as a small bump in each of them rather than one obvious spike. An explicit grouping
 * hash keeps like with like:
 *
 * - No response at all (status 0) is one group per host and host probe result, whatever
 *   the endpoint. It is usually the visitor's network, a blocker, or something at our
 *   edge answering before the app can add CORS headers, so what matters is the rate, and
 *   a fixed set of groups gives that rate a visible baseline. The probe result separates
 *   our edge from the visitor's network; see reportNoResponse below. The endpoints are
 *   kept in metadata.
 * - A timeout is grouped per endpoint. Only requests given a `timeout` option can time
 *   out, and today no request reaches this: the only one with a timeout, the admin
 *   What's Included check, handles its own errors on purpose so that its expected
 *   retries go unreported. A slow endpoint otherwise shows up as status 0 once the
 *   server gives up on it. The branch is kept for any later request that sets a timeout
 *   and leaves reporting to this handler.
 * - A real HTTP response is grouped per method, endpoint and status, so a 500 from one
 *   endpoint is its own group and is not buried under network noise. A parse error on a
 *   successful response means we sent something the page cannot read, so it is an error.
 *
 * The request's URL and method come from what the prefilter below recorded on the jqXHR.
 * Failing that, `context` is `this` in the error callback: the jQuery ajax settings
 * object, unless the caller passed its own `context` option. That could be anything,
 * including a form input whose `type` is 'text', so it is only read when it has the
 * settings object's `dataTypes` array, and the request is otherwise recorded as unknown. */
const describeAjaxFailure = (jqXHR, textStatus, context) => {
  const settings = jqXHR.adcRequest || (context && Array.isArray(context.dataTypes) ? context : null);
  const method = settings ? (settings.type || settings.method || 'GET').toUpperCase() : 'unknown';
  const parsed = settings && typeof settings.url === 'string' ? parseAjaxUrl(settings.url) : null;
  const endpoint = parsed ? normalizeAjaxEndpoint(parsed) : 'unknown';
  const host = parsed ? parsed.host : 'unknown';
  const origin = parsed ? parsed.origin : null;
  const status = jqXHR.status;

  if (textStatus === 'timeout') {
    return {
      method,
      endpoint,
      host,
      origin,
      message: `AJAX timeout: ${method} ${endpoint}`,
      groupingHash: `ajax-timeout ${method} ${endpoint}`,
      severity: 'warning'
    };
  }

  if (!status) {
    return {
      method,
      endpoint,
      host,
      origin,
      message: `AJAX request got no response from ${host}`,
      groupingHash: `ajax-no-response ${host}`,
      severity: 'info'
    };
  }

  return {
    method,
    endpoint,
    host,
    origin,
    message: `AJAX ${status} (${textStatus}): ${method} ${endpoint}`,
    groupingHash: `ajax-http ${status} ${method} ${endpoint}`,
    severity: status >= 500 || textStatus === 'parsererror' ? 'error' : 'warning'
  };
};

/* Ask whether a host answers at all, with a no-cors request to its root. A no-cors fetch
 * sends no preflight and needs no CORS headers, so it resolves on any HTTP response,
 * including a WAF block page or a gateway error, and rejects only when nothing came back.
 *
 * 'reachable' therefore points at CORS, a failed preflight or a block at our edge rather
 * than the visitor's connection, and 'unreachable' at the network, DNS or a blocker. A
 * blocker that matches API paths but not the host root would read as reachable. */
const probeHost = (origin) => {
  if (!origin) {
    return Promise.resolve('skipped');
  }
  if (typeof fetch !== 'function') {
    return Promise.resolve('unsupported');
  }

  const controller = typeof AbortController === 'function' ? new AbortController() : null;
  let timer;
  const timedOut = new Promise((resolve) => {
    timer = setTimeout(() => {
      if (controller) {
        controller.abort();
      }
      resolve('timeout');
    }, HOST_PROBE_TIMEOUT_MS);
  });
  const answered = fetch(origin + '/', {
    mode: 'no-cors',
    cache: 'no-store',
    credentials: 'omit',
    signal: controller ? controller.signal : undefined
  }).then(() => 'reachable', () => 'unreachable');

  return Promise.race([answered, timedOut]).then((result) => {
    clearTimeout(timer);
    return result;
  });
};

const sendAjaxReport = (report, metadata) => {
  Bugsnag.notify(new Error(report.message), function (event) {
    event.errors[0].errorClass = 'AjaxError';
    event.groupingHash = report.groupingHash;
    event.severity = report.severity;
    event.addMetadata('ajax', metadata);
  });
};

/* Report status 0 once per host per page load.
 *
 * A visitor whose browser cannot reach a host fails every call the page makes to it, and
 * reporting each one would make the group's rate track how many calls each page type
 * makes. One report per page load makes it a count of affected page loads, which can be
 * compared with page views. The first failure starts a probe of the host; failures that
 * arrive before the probe settles are counted into the same report, and any after that
 * are dropped.
 *
 * The probe can take up to 3 seconds, and a visitor looking at a broken page may well
 * leave before then, so a report still waiting is sent as the page is hidden or torn
 * down, with the probe recorded as interrupted. Bugsnag sends over XHR, which a browser
 * may cancel during teardown, so this is best effort.
 *
 * The report is grouped by the probe result as well as the host, because the results
 * point at different owners. 'reachable' means the host answered but the request did
 * not get through, which is a CORS break or a block at our edge and ours to fix; the
 * HTTP 421 refusals fixed on 2026-09-28 showed up there. 'unreachable' is the
 * visitor's network, DNS, a blocker, or a host that is down for everyone, and it also
 * collects crawlers that cannot reach us at all. Sharing one group let crawler traffic
 * reopen a fixed error and would have hidden a returning edge problem among it. */
const reportNoResponse = (report, metadata) => {
  const existing = noResponseReports[report.host];
  if (existing) {
    if (!existing.sent) {
      existing.laterFailures += 1;
      if (existing.endpoints.indexOf(report.endpoint) === -1 && existing.endpoints.length < 10) {
        existing.endpoints.push(report.endpoint);
      }
    }
    return;
  }

  const entry = { sent: false, laterFailures: 0, endpoints: [report.endpoint] };
  entry.send = (hostProbe) => {
    if (entry.sent) {
      return;
    }
    entry.sent = true;
    sendAjaxReport(Object.assign({}, report, {
      message: `${report.message} (host probe: ${hostProbe})`,
      groupingHash: `${report.groupingHash} ${hostProbe}`
    }), Object.assign({}, metadata, {
      hostProbe,
      laterFailures: entry.laterFailures,
      endpoints: entry.endpoints
    }));
  };
  noResponseReports[report.host] = entry;
  probeHost(report.origin).then(entry.send);
};

const flushNoResponseReports = () => {
  Object.keys(noResponseReports).forEach((host) => noResponseReports[host].send('interrupted'));
};

/* Set authorization token for all API request. */
const apiAjaxDefaults = {
  headers: {
    'X-Requested-With': 'XMLHttpRequest'
  },
  error: function (jqXHR, textStatus, errorThrown) {
    // The response is deliberately not logged. Bugsnag records console calls as
    // breadcrumbs, and a body can hold what the visitor submitted; the browser's Network
    // panel shows it to anyone debugging.

    // responseJSON is null for a JSON null body, which would otherwise throw here and
    // stop the failure being reported at all.
    if (jqXHR.responseJSON && jqXHR.responseJSON.errors) {
      const errors = Object.entries(jqXHR.responseJSON.errors).map((v) => v[1]);
      if ($.notify) {
        $.notify({ message: '<span style="text-decoration:underline;font-weight:bold;">Error:</span> <br>' + errors.join('<br>') }, { type: 'danger' });
      } else {
        alert('<span style="text-decoration:underline;font-weight:bold;">Error:</span> \n' + errors.join('\n'));
      }
    }

    // jQuery only reports 'abort' when our own code calls jqXHR.abort(), such as a search
    // cancelling the request it superseded; a request the browser cancels arrives as
    // 'error' with status 0. Nothing failed, so there is nothing to report.
    if (textStatus === 'abort') {
      return;
    }

    // A status of 0 means the request never got as far as a response. When the page is
    // being torn down or is already hidden, that is simply the visitor leaving with a
    // request in flight: there is no server response behind it and nothing on our end to
    // fix, so reporting it buries real failures and consumes Bugsnag event quota. A
    // status of 0 while the page is still up and visible is deliberately left reported,
    // because that is also what a CORS break or an unreachable API looks like.
    const abandonedByVisitor = jqXHR.status === 0 &&
      (pageIsUnloading || document.visibilityState === 'hidden');

    if (abandonedByVisitor || typeof Bugsnag === 'undefined' || !Bugsnag.notify) {
      return;
    }

    const report = describeAjaxFailure(jqXHR, textStatus, this);
    const metadata = {
      method: report.method,
      endpoint: report.endpoint,
      textStatus,
      status: jqXHR.status,
      response: summarizeResponse(jqXHR),
      errorThrown,
      // Recorded so the status 0 reports that survive the filter above can be sorted
      // into genuine failures versus visitors leaving in a way pagehide did not catch.
      visibility: document.visibilityState,
      pageIsUnloading
    };

    if (!jqXHR.status && textStatus !== 'timeout') {
      reportNoResponse(report, metadata);
    } else {
      sendAjaxReport(report, metadata);
    }
  }
  // No Authorization header here any more: a signed-in user's API_URL is this site's api-proxy,
  // which adds their token on the server, so the token never reaches the page (#4329).
};

if (typeof jQuery === 'undefined') {
  reportMissingJQuery();
} else {
  $.ajaxSetup(apiAjaxDefaults);

  /* Record each request's URL and method on its jqXHR, which the error callback receives
   * whatever options the caller passed. `this` in that callback stops being the settings
   * object as soon as a caller sets `context`, which 19 requests do, the public tour search
   * among them; without this their failures would be filed under an unknown host. */
  $.ajaxPrefilter((options, originalOptions, jqXHR) => {
    jqXHR.adcRequest = { url: options.url, type: options.type };
  });
}
